import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./PrismPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Prism" — an ONBOARDING paywall. The pitch is NOT security / backup / search
// stated as a flat checklist. It is a single, cinematic metaphor: a beam of
// your own camera roll enters a glass prism and is REFRACTED into a spectrum,
// where each band of colour is one power the app grants your library. The
// paywall is the last step of setup — "you're all set, here's what your
// library becomes when the light passes through."
//
// No prior paywall used refraction / a spectrum-as-value-stack, nor an
// incoming beam built from the user's real memories. Brand blue anchors the
// centre of the spectrum; jewel tones fan out from it, so the palette is the
// house palette used a way nothing else did. Fully self-contained on debug
// data; memory photos pre-fetched into public/prism.
//
// Yearly is the hero (large sum, per-month broken out, savings badge, the
// default selection). Monthly is offered small. A transparent free-trial
// timeline sits above pricing for trust + App Store compliance. Copy never
// implies photos are lost when a subscription ends.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const mem = (n: number) => `${PUB}/prism/mem${n}.jpg`

// The incoming beam — a strip of the user's memories, doubled for a seamless
// marquee. These are "the white light" that the prism will refract.
const MEMORIES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

type Band = {
  key: string
  color: string
  title: string
  desc: string
  Icon: (p: { className?: string }) => JSX.Element
}

// Hand-built stroke icons so each tints to its own spectrum band.
const IconVault = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect
      x="4"
      y="10"
      width="16"
      height="11"
      rx="2.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M8 10V7.5a4 4 0 0 1 8 0V10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <circle cx="12" cy="15.4" r="1.7" fill="currentColor" />
  </svg>
)
const IconFull = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path
      d="M5.5 15.5A3.5 3.5 0 0 1 6 8.6a5 5 0 0 1 9.6-1.3A4 4 0 0 1 18 15.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 15.5l2.4 2.4L16 13.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const IconEverywhere = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect
      x="3"
      y="5"
      width="13"
      height="10"
      rx="1.8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <rect
      x="15"
      y="10"
      width="6"
      height="9"
      rx="1.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M7 19h5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)
const IconOrganize = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect
      x="4"
      y="4"
      width="7"
      height="7"
      rx="1.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <rect
      x="13"
      y="4"
      width="7"
      height="7"
      rx="1.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <rect
      x="4"
      y="13"
      width="7"
      height="7"
      rx="1.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M16.5 13.5v6M13.5 16.5h6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)
const IconHidden = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path
      d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6a9.7 9.7 0 0 1-5-1.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="12"
      cy="12"
      r="2.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M4 20L20 4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)
const IconRecover = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path
      d="M5 8.5A7.5 7.5 0 1 1 4.2 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M4 4.5V9h4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 8.8V12l2.4 1.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// The spectrum — brand blue sits at the centre; jewel tones fan out from it.
const BANDS: Band[] = [
  {
    key: "private",
    color: "#8b5cf6",
    title: "A space that's only yours",
    desc: "A private, PIN-locked vault the rest of your phone can't see.",
    Icon: IconVault,
  },
  {
    key: "full",
    color: "#2b5ff5",
    title: "Every photo, full resolution",
    desc: "Encrypted backup of the originals — nothing downscaled, ever.",
    Icon: IconFull,
  },
  {
    key: "everywhere",
    color: "#12a8c4",
    title: "On every device you own",
    desc: "Your library syncs in seconds, phone to tablet to the web.",
    Icon: IconEverywhere,
  },
  {
    key: "organize",
    color: "#1eb87d",
    title: "Sorted the moment they land",
    desc: "Smart albums, faces, and duplicate cleanup — handled for you.",
    Icon: IconOrganize,
  },
  {
    key: "hidden",
    color: "#f2994a",
    title: "Hidden in plain sight",
    desc: "A decoy vault and break-in alerts if anyone comes looking.",
    Icon: IconHidden,
  },
  {
    key: "recover",
    color: "#f2637a",
    title: "Nothing ever lost",
    desc: "Recover a deleted moment — or your PIN — whenever you need to.",
    Icon: IconRecover,
  },
]

function PrismPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [launching, setLaunching] = useState(false)
  const [showClose, setShowClose] = useState(false)
  // Bump on prism tap to re-fire the refraction animation.
  const [pulse, setPulse] = useState(0)
  const closeTimer = useRef<number | undefined>(undefined)

  // Onboarding paywalls shouldn't trap — reveal the dismiss control after a
  // beat so the value lands first (and it's always reachable for compliance).
  useEffect(() => {
    if (reduce) {
      setShowClose(true)
      return
    }
    closeTimer.current = window.setTimeout(() => setShowClose(true), 2600)
    return () => window.clearTimeout(closeTimer.current)
  }, [reduce])

  const handleClose = () => native.postNativeEvent("close")
  const refract = useCallback(() => {
    if (reduce) return
    setPulse((p) => p + 1)
  }, [reduce])

  const handleSubscribe = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    setLaunching(true)
    window.setTimeout(
      () => {
        if (p) native.subscribe(p)
      },
      reduce ? 0 : 640,
    )
  }

  const ctaLabel =
    plan === "yearly"
      ? `Start my ${trialDays}-day free trial`
      : `Continue monthly — ${monthlyPrice}/mo`

  const ctaSub =
    plan === "yearly"
      ? `Then ${yearlyPrice}/year (just ${yearlyPerMonth}/mo). Cancel anytime.`
      : `Billed ${monthlyPrice} every month. Cancel anytime.`

  return (
    <div className={styles.root}>
      {/* ambient light — soft aurora glows + drifting dust */}
      <div className={styles.ambient} aria-hidden>
        <span className={`${styles.glow} ${styles.glowA}`} />
        <span className={`${styles.glow} ${styles.glowB}`} />
        <span className={`${styles.glow} ${styles.glowC}`} />
        <div className={styles.dust}>
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} style={{ ["--i" as any]: i }} />
          ))}
        </div>
      </div>

      <div className={styles.scroll}>
        {/* ── Top bar: onboarding progress + delayed dismiss ───────────── */}
        <div className={styles.topbar}>
          <span className={styles.setupPill}>
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
              <path
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Setup complete
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Hero: the beam, the prism, the spectrum ──────────────────── */}
        <header className={styles.hero}>
          {/* the incoming beam of memories */}
          <div className={styles.beam} aria-hidden>
            <div className={styles.beamTrack}>
              {[...MEMORIES, ...MEMORIES].map((n, i) => (
                <span className={styles.beamTile} key={i}>
                  <img src={mem(n)} alt="" loading="lazy" />
                </span>
              ))}
            </div>
            <div className={styles.beamFade} />
          </div>

          {/* the funnel + prism + refracted spectrum (one continuous light) */}
          <button
            className={styles.prismStage}
            onClick={refract}
            aria-label="Refract"
            key={pulse}
          >
            <svg
              className={styles.prismSvg}
              viewBox="0 0 320 260"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <linearGradient id="pw-glass" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#ffffff" stopOpacity="0.9" />
                  <stop offset="0.5" stopColor="#bcd0ff" stopOpacity="0.55" />
                  <stop offset="1" stopColor="#2b5ff5" stopOpacity="0.35" />
                </linearGradient>
                <linearGradient id="pw-white" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
                  <stop offset="1" stopColor="#ffffff" stopOpacity="0.95" />
                </linearGradient>
                {BANDS.map((b, i) => (
                  <linearGradient
                    key={b.key}
                    id={`pw-ray-${i}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0" stopColor={b.color} stopOpacity="0.95" />
                    <stop offset="1" stopColor={b.color} stopOpacity="0" />
                  </linearGradient>
                ))}
                <radialGradient id="pw-flare" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
                  <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* incoming white beam converging to the prism apex */}
              <path
                className={styles.whiteBeam}
                d="M150 0 H170 L166 96 H154 Z"
                fill="url(#pw-white)"
              />

              {/* the refracted spectrum fanning out below the prism */}
              <g className={styles.rays}>
                {BANDS.map((b, i) => {
                  const spread = (i - (BANDS.length - 1) / 2) * 22
                  const x = 160 + spread
                  const w = 15
                  return (
                    <path
                      key={b.key}
                      className={styles.ray}
                      style={{ ["--d" as any]: `${i * 90}ms` }}
                      d={`M${160 - 7} 150 L${160 + 7} 150 L${x + w} 256 L${
                        x - w
                      } 256 Z`}
                      fill={`url(#pw-ray-${i})`}
                    />
                  )
                })}
              </g>

              {/* the glass prism */}
              <g className={styles.prismBody}>
                <path
                  d="M160 92 L206 150 H114 Z"
                  fill="url(#pw-glass)"
                  stroke="#ffffff"
                  strokeOpacity="0.65"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
                <path
                  d="M160 92 L206 150"
                  stroke="#ffffff"
                  strokeOpacity="0.85"
                  strokeWidth="1.4"
                />
              </g>

              {/* the flare where the beam strikes the glass */}
              <circle
                className={styles.flare}
                cx="160"
                cy="120"
                r="30"
                fill="url(#pw-flare)"
              />
            </svg>
          </button>

          <p className={styles.eyebrow}>Welcome to Prism Pro</p>
          <h1 className={styles.title}>
            See everything you've kept
            <br />
            <span className={styles.titleGrad}>in a whole new light.</span>
          </h1>
          <p className={styles.sub}>
            One tap turns your camera roll into a private library — backed up in
            full resolution, sorted for you, and yours on every screen.
          </p>
        </header>

        {/* ── The spectrum, itemized ───────────────────────────────────── */}
        <ul className={styles.bands}>
          {BANDS.map((b, i) => (
            <li
              className={styles.band}
              key={b.key}
              style={{
                ["--c" as any]: b.color,
                ["--d" as any]: `${180 + i * 90}ms`,
              }}
            >
              <span className={styles.bandBar} aria-hidden />
              <span className={styles.bandIcon}>
                <b.Icon className={styles.bandGlyph} />
              </span>
              <span className={styles.bandText}>
                <span className={styles.bandTitle}>{b.title}</span>
                <span className={styles.bandDesc}>{b.desc}</span>
              </span>
            </li>
          ))}
        </ul>

        {/* ── Trust ────────────────────────────────────────────────────── */}
        <div className={styles.trust}>
          <div className={styles.stars} aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l2.7 5.9 6.4.7-4.8 4.3 1.3 6.3L12 17l-5.6 2.8 1.3-6.3-4.8-4.3 6.4-.7z"
                  fill="currentColor"
                />
              </svg>
            ))}
          </div>
          <p>
            <strong>4.9</strong> · joining 4M+ private libraries
          </p>
        </div>

        {/* ── Trial timeline (trust + compliance) ──────────────────────── */}
        <section
          className={styles.timeline}
          aria-label="How your free trial works"
        >
          <h2 className={styles.timelineHead}>
            How your {trialDays}-day free trial works
          </h2>
          <ol className={styles.steps}>
            <li className={styles.step}>
              <span className={styles.stepDot} data-i="0" aria-hidden>
                <svg viewBox="0 0 24 24" width="15" height="15">
                  <path
                    d="M7 11V8a5 5 0 0 1 9.5-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <rect
                    x="5"
                    y="11"
                    width="14"
                    height="9"
                    rx="2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
              </span>
              <span className={styles.stepBody}>
                <b>Today</b>
                <span>
                  Everything unlocks. Your whole library, backed up in full
                  resolution.
                </span>
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepDot} data-i="1" aria-hidden>
                <svg viewBox="0 0 24 24" width="15" height="15">
                  <path
                    d="M6 16v-4a6 6 0 0 1 12 0v4l1.5 2H4.5z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 20a2 2 0 0 0 4 0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.stepBody}>
                <b>Day {Math.max(1, trialDays - 2)}</b>
                <span>
                  We'll remind you before your trial ends. No surprises.
                </span>
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepDot} data-i="2" aria-hidden>
                <svg viewBox="0 0 24 24" width="15" height="15">
                  <path
                    d="M12 3.2l2.5 5.6 6 .6-4.5 4 1.3 5.9L12 16.9 6.7 19.3 8 13.4 3.5 9.4l6-.6z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span className={styles.stepBody}>
                <b>Day {trialDays}</b>
                <span>
                  Your yearly plan begins — just {yearlyPerMonth}/mo. Cancel any
                  time before.
                </span>
              </span>
            </li>
          </ol>
        </section>

        {/* ── Pricing — yearly is the hero ─────────────────────────────── */}
        <section className={styles.pricing} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.planGlow} aria-hidden />
            <span className={styles.planTop}>
              <span className={styles.planName}>Yearly</span>
              {savings > 0 && (
                <span className={styles.saveTag}>Save {savings}%</span>
              )}
            </span>
            <span className={styles.priceRow}>
              <span className={styles.priceMain}>{yearlyPrice}</span>
              <span className={styles.pricePer}>/year</span>
            </span>
            <span className={styles.priceFoot}>
              just <b>{yearlyPerMonth}/mo</b>, billed yearly ·{" "}
              <span className={styles.trialFoot}>{trialDays} days free</span>
            </span>
            <span className={styles.radio} aria-hidden>
              <span className={styles.radioDot} />
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planTop}>
              <span className={styles.planName}>Monthly</span>
            </span>
            <span className={styles.priceRowSm}>
              <span className={styles.priceMainSm}>{monthlyPrice}</span>
              <span className={styles.pricePerSm}>/month</span>
            </span>
            <span className={styles.radio} aria-hidden>
              <span className={styles.radioDot} />
            </span>
          </button>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <div className={styles.ctaWrap}>
          <button
            className={`${styles.cta} ${launching ? styles.ctaLaunch : ""}`}
            onClick={handleSubscribe}
          >
            <span className={styles.ctaSheen} aria-hidden />
            <span className={styles.ctaText}>{ctaLabel}</span>
            <svg
              className={styles.ctaArrow}
              viewBox="0 0 24 24"
              width="19"
              height="19"
              aria-hidden
            >
              <path
                d="M5 12h13M13 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos stay yours — always. Cancel and they're still right
            here; nothing is ever locked away.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrismPaywall
